import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useSkin from "../../hooks/useSkin";
import useSkinPath from "../../hooks/useSkinPath";
import { StudentListMode } from "../../redux/actions/reports/types";
import { StyledActivityFeed } from "./Dashboard.styles";

export type ActivityItemType = "book_completed" | "quiz_completed" | "assignment_started" | "reading_session";

export interface ActivityItem {
  id: string;
  studentName: string;
  action: string;
  timestamp: string;
  type: ActivityItemType;
}

interface Props {
  activities: ActivityItem[];
  isLoading?: boolean;
}

const MAX_ITEMS = 8;

const getInitials = (name: string): string => {
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

const ActivityFeed: React.FC<Props> = ({ activities, isLoading }) => {
  const { t } = useTranslation();
  const { isGriffle } = useSkin();
  const { path } = useSkinPath();

  const visibleActivities = activities.slice(0, MAX_ITEMS);

  return (
    <StyledActivityFeed $isGriffle={isGriffle}>
      <div className="feed-header">
        <h2 className="feed-title">{t("dashboard_activity_feed_title", "Recent Activity")}</h2>
        <Link
          className="feed-view-all"
          to={path("/progress-report-students")}
          state={{ mode: StudentListMode.ALL_STUDENTS }}
        >
          {t("dashboard_activity_feed_view_all", "View all")}
        </Link>
      </div>

      {isLoading ? (
        <div className="feed-loading" style={{ textAlign: "center", padding: "2rem 0", color: "#999" }}>
          {t("common_loading", "Loading...")}
        </div>
      ) : visibleActivities.length === 0 ? (
        <div className="feed-empty" style={{ textAlign: "center", padding: "2rem 0", color: "#999" }}>
          {t("dashboard_activity_feed_empty", "No recent activity")}
        </div>
      ) : (
        <ul className="feed-list">
          {visibleActivities.map((item) => (
            <li key={item.id} className="feed-item">
              <div className="feed-avatar">{getInitials(item.studentName)}</div>
              <div className="feed-content">
                <span className="feed-student-name">{item.studentName}</span>
                <p className="feed-action">{item.action}</p>
              </div>
              <span className="feed-timestamp">{item.timestamp}</span>
            </li>
          ))}
        </ul>
      )}
    </StyledActivityFeed>
  );
};

export default ActivityFeed;
